import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { CheckCircle2, FileUp, ShieldAlert, Upload, MapPin, Building2 } from "lucide-react";
import { toast } from "sonner";

const FLOWQA_LOGO = "/brand/flowqa-logo-full.png";

export default function SurveyUpload() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [req, setReq] = useState(null);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ surveyor_name: "", surveyor_company: "", surveyor_role: "", comments: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/survey-request/${token}`);
        if (mounted) {
          setReq(data);
          setForm((f) => ({ ...f, surveyor_name: data?.surveyor_name || "", surveyor_company: data?.surveyor_company || "" }));
        }
      } catch (e) {
        if (mounted) setError(e?.response?.data?.detail || "This link is invalid or expired.");
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [token]);

  const submit = async () => {
    if (!file) { toast.error("Please pick a file"); return; }
    if (!form.surveyor_name) { toast.error("Your name is required"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("surveyor_name", form.surveyor_name);
      fd.append("surveyor_company", form.surveyor_company || "");
      fd.append("surveyor_role", form.surveyor_role || "");
      fd.append("comments", form.comments || "");
      await axios.post(`${API}/public/survey-request/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="grid min-h-screen place-items-center bg-slate-50 text-slate-500">Loading FlowQA…</div>;
  }
  if (error || !req) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-50 p-4">
        <Card className="max-w-md border-rose-200 bg-rose-50/40 p-6 text-center" data-testid="survey-upload-error">
          <ShieldAlert className="mx-auto mb-3 size-8 text-rose-600" />
          <h2 className="font-heading text-xl font-black tracking-tighter text-slate-900">Link unavailable</h2>
          <p className="mt-2 text-sm text-slate-600">{error || "This upload link is invalid or has expired."}</p>
        </Card>
      </div>
    );
  }
  if (done || req.status === "Received") {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-50 p-4">
        <Card className="max-w-md border-emerald-200 bg-emerald-50/40 p-6 text-center" data-testid="survey-upload-done">
          <CheckCircle2 className="mx-auto mb-3 size-10 text-emerald-600" />
          <h2 className="font-heading text-2xl font-black tracking-tighter text-slate-900">Thanks — report received</h2>
          <p className="mt-2 text-sm text-slate-600">Your {req.document_type} has been uploaded to FlowQA. The requester has been notified.</p>
          <p className="mt-4 text-xs text-slate-500">Powered by FlowQA</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-slate-900 text-white">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <img src={FLOWQA_LOGO} alt="FlowQA" className="h-9 w-auto object-contain" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-teal-300">Secure surveyor upload</div>
        </div>
      </div>
      <div className="mx-auto max-w-3xl px-4 py-8 sm:py-12" data-testid="survey-upload-page">
        <div className="overline text-teal-700">{req.document_type}</div>
        <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">Upload {req.document_type}</h1>
        <p className="mt-1 text-sm text-slate-600">Requested by {req.requested_by_name} via FlowQA. No login required.</p>

        <Card className="mt-6 border-slate-200 p-5">
          <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
            <Meta label="Project" value={req.project_name} />
            <Meta label="Lot Number" value={req.lot_number} />
            <Meta label="ITP" value={[req.itp_number, req.itp_name].filter(Boolean).join(" — ")} />
            <Meta label="Location / Chainage" value={req.location} icon={MapPin} />
            <Meta label="Drawing" value={req.drawing_number} />
            <Meta label="Due Date" value={req.due_date} />
            <Meta label="Surveyor" value={[req.surveyor_name, req.surveyor_company].filter(Boolean).join(" · ")} icon={Building2} />
          </div>
          {req.request_message && (
            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <div className="overline text-slate-500">Message</div>
              <div className="mt-1 whitespace-pre-line">{req.request_message}</div>
            </div>
          )}
        </Card>

        <Card className="mt-6 border-slate-200 p-5">
          <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">Upload report</h2>
          <p className="mt-1 text-xs text-slate-500">PDF, DOCX, XLSX, JPG or PNG.</p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>File *</Label>
              <Input data-testid="survey-file-input" type="file" accept=".pdf,.docx,.doc,.xlsx,.xls,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)} className="h-11" />
            </div>
            <div>
              <Label>Your name *</Label>
              <Input data-testid="survey-name-input" value={form.surveyor_name} onChange={(e) => setForm({ ...form, surveyor_name: e.target.value })} />
            </div>
            <div>
              <Label>Your company</Label>
              <Input data-testid="survey-company-input" value={form.surveyor_company} onChange={(e) => setForm({ ...form, surveyor_company: e.target.value })} />
            </div>
            <div>
              <Label>Your role / title</Label>
              <Input data-testid="survey-role-input" value={form.surveyor_role} onChange={(e) => setForm({ ...form, surveyor_role: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Comments (optional)</Label>
              <textarea data-testid="survey-comments-input" rows={3} value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" />
            </div>
          </div>
          <Button data-testid="survey-submit-btn" onClick={submit} disabled={submitting || !file || !form.surveyor_name} className="mt-5 h-11 w-full bg-teal-700 hover:bg-teal-800">
            {submitting ? "Uploading…" : <><Upload className="mr-2 size-4" /> Upload report</>}
          </Button>
          <p className="mt-3 text-center text-[10px] uppercase tracking-wider text-slate-500">Powered by FlowQA</p>
        </Card>
      </div>
    </div>
  );
}

function Meta({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
        {Icon && <Icon className="size-3" />}{label}
      </div>
      <div className="mt-0.5 text-sm font-semibold text-slate-900">{value || <span className="text-slate-400">Not specified</span>}</div>
    </div>
  );
}
